import _ from 'lodash';
import uuid from 'uuid';
import React from 'react';
// @ts-ignore JS package
import pluralize from 'pluralize';
import {
  InlineFieldSet,
  TextCollectionFieldType,
  UnitField,
  TextCollectionField,
  UnitFieldTypes,
} from '@scale/shared/types/params/textcollection';
import {
  FieldSetAnnotation,
  UnitFieldAnnotation,
  TextCollectionAnnotation,
  TextFieldAnnotation,
  NumberFieldAnnotation,
} from '@scale/shared/types/response/textcollection';
import { TextCollectionAnnotationsOrCallback } from '@scale/shared/types/textCollection';
// TODO: Import @material-ui from design-system folder
/* eslint-disable restrict-imports/restrict-imports */
import { makeStyles, IconButton, InputAdornment } from '@material-ui/core';
import classnames from 'classnames';
import {
  getFieldMaxResponses,
  getEmptyFieldSetResponse,
  getDefaultAnnotation,
  getFieldMinResponses,
} from '@scale/shared/textcollection/defaults';
import CustomTextField from './CustomTextField';
import ReactTooltip from 'react-tooltip';
import { TextButton } from 'react-scale/components/design-system';
import { Select, Option } from 'design-system/Select';

interface InlineFieldSetSectionProps {
  field: InlineFieldSet;
  annotation: FieldSetAnnotation;
  setAnnotation: (annotation: FieldSetAnnotation) => void;
  setAnnotationIndex: (
    field: TextCollectionField,
    response: TextCollectionAnnotationsOrCallback,
    index: number,
  ) => void;
  deleteAnnotationIndex: (field: TextCollectionField, index: number) => void;
  pushAnnotations: (
    field: TextCollectionField,
    response: Record<string, TextCollectionAnnotation>,
  ) => void;
  quickAddResponses: Record<string, TextCollectionAnnotation>[];
  disabled?: boolean;
}

export default function InlineFieldSetSection(props: InlineFieldSetSectionProps) {
  const styles = useFieldSetStyles(props);

  const responseCount = React.useRef(props.annotation.response.length);
  const maxResponses = getFieldMaxResponses(props.field);
  const minResponses = getFieldMinResponses(props.field);

  const addEmptyResponse = React.useCallback(() => {
    if (responseCount.current < maxResponses) {
      props.pushAnnotations(props.field, getEmptyFieldSetResponse(props.field));
      responseCount.current += 1;
    }
  }, [props.pushAnnotations, props.field, maxResponses, responseCount]);

  const changeResponse = React.useCallback(
    (
      index: number,
      currentResponse: Record<string, TextCollectionAnnotation>,
      fieldId: string,
      value: string,
    ) => {
      const newResponse: Record<string, TextCollectionAnnotation> = {
        ...currentResponse,
        [fieldId]: {
          ...(currentResponse[fieldId] as TextFieldAnnotation | NumberFieldAnnotation),
          response: [value],
        },
      };

      props.setAnnotationIndex(props.field, newResponse, index);
    },
    [props.setAnnotationIndex, props.field],
  );

  const removeResponse = React.useCallback(
    (index: number) => {
      const clearFields: TextCollectionField[] = [];
      if (responseCount.current <= 2) {
        for (const subField of props.field.fields) {
          if (subField.hide_for_single_response) {
            clearFields.push(subField);
          }
        }
        const callback = (oldResponse: Record<string, TextCollectionAnnotation>) => {
          const newResponse = {
            ...oldResponse,
          };
          for (const field of clearFields) {
            newResponse[field.field_id] = getDefaultAnnotation(field);
          }
          return newResponse;
        };
        props.setAnnotationIndex(props.field, callback, 0);
      }
      props.deleteAnnotationIndex(props.field, index);
      responseCount.current -= 1;
    },
    [props.setAnnotationIndex, props.deleteAnnotationIndex, props.field],
  );

  React.useEffect(() => {
    if (responseCount.current === 0 && minResponses > 0) {
      addEmptyResponse();
      responseCount.current = 1;
    }
  }, [responseCount, minResponses, addEmptyResponse]);

  const blurredFieldsRef = React.useRef<Record<string, boolean>>({});
  const setBlurredAtUuid = React.useCallback(
    (uuid: string) => {
      blurredFieldsRef.current[uuid] = true;
    },
    [blurredFieldsRef],
  );

  const filteredUnitFields = React.useMemo(
    () =>
      props.field.fields
        .filter(field => UnitFieldTypes.includes(field.type))
        .filter(field => !(field.hide_for_single_response && responseCount.current <= 1)),
    [props.field, responseCount.current <= 1],
  );

  let quickAddText: string | undefined;
  if (responseCount.current <= 1) {
    if (props.quickAddResponses.length === 1) {
      const copyTitle = _.first(
        _.values(props.quickAddResponses[0]).filter(
          annot => annot.response.length > 0 && annot.response[0],
        ),
      )?.response?.[0];
      if (copyTitle) {
        quickAddText = `Copy "${copyTitle}"`;
      }
    } else if (props.quickAddResponses.length > 1) {
      quickAddText = `Copy ${props.quickAddResponses.length} ${_.lowerCase(
        pluralize(props.field.title, 2),
      )}`;
    }
  }

  const quickAddResponses = React.useCallback(() => {
    if (responseCount.current <= 1) {
      const fieldSetAnnotation: FieldSetAnnotation = {
        type: TextCollectionFieldType.FieldSet,
        field_id: props.field.field_id,
        response: _.cloneDeep(props.quickAddResponses),
        uuids: props.quickAddResponses.map(() => uuid.v4()),
      };
      props.setAnnotation(fieldSetAnnotation);
      responseCount.current = fieldSetAnnotation.response.length;
    }
  }, [props.setAnnotation, props.quickAddResponses, props.field]);

  const uuids = props.annotation.uuids ?? [];

  return (
    <div className={styles.fieldSet}>
      {props.annotation.response.map((annotations, index) => (
        <InlineFieldSetEntry
          key={uuids[index]}
          index={index}
          uuid={props.annotation.uuids?.[index] || ''}
          field={props.field}
          annotations={annotations}
          quickAddText={index === 0 ? quickAddText : undefined}
          quickAddResponses={index === 0 ? quickAddResponses : undefined}
          blurred={!!blurredFieldsRef.current[uuids[index]]}
          filteredUnitFields={filteredUnitFields}
          disabled={!!props.disabled}
          showDeleteButton={index > 0 && responseCount.current > 1}
          showAddButton={index === 0 && responseCount.current < maxResponses}
          onBlur={setBlurredAtUuid}
          changeResponse={changeResponse}
          addEmptyResponse={index === 0 ? addEmptyResponse : undefined}
          delete={removeResponse}
        />
      ))}
      {responseCount.current === 0 && (
        <div>
          <TextButton
            startIcon={<PlusIcon />}
            classes={{ root: styles.inlineButton }}
            disabled={props.disabled || responseCount.current >= maxResponses}
            onClick={addEmptyResponse}
          >
            Add {props.field.title}
          </TextButton>
        </div>
      )}
    </div>
  );
}

interface InlineFieldSetEntryProps {
  index: number;
  uuid: string;
  annotations: Record<string, TextCollectionAnnotation>;
  filteredUnitFields: UnitField[];
  field: TextCollectionField;
  blurred: boolean;
  disabled: boolean;
  showAddButton: boolean;
  showDeleteButton: boolean;
  quickAddText?: string;
  quickAddResponses?: () => void;
  onBlur: (uuid: string) => void;
  changeResponse: (
    index: number,
    currentResponse: Record<string, TextCollectionAnnotation>,
    fieldId: string,
    value: string,
  ) => void;
  addEmptyResponse?: () => void;
  delete: (index: number) => void;
}

const InlineFieldSetEntry: React.FC<InlineFieldSetEntryProps> = React.memo(props => {
  const onBlur = React.useCallback(() => props.onBlur(props.uuid), [props.onBlur, props.uuid]);

  const styles = useFieldSetStyles(props);
  return (
    <div key={props.index} className={styles.inlineFieldSetResponse}>
      <div className={styles.inlineFields}>
        {props.filteredUnitFields.map((subField, j) => {
          const annotation = props.annotations[subField.field_id] as UnitFieldAnnotation;
          const isBlank = !annotation?.response?.[0]?.toString()?.trim();
          let errorText;
          if (props.blurred && subField.required && isBlank) {
            errorText = 'This field cannot be empty';
          }
          const requiredStr = subField.required ? ' *' : '';
          const title = `${subField.title}${requiredStr}`;
          const inputType = subField.type === TextCollectionFieldType.Number ? 'number' : 'text';

          if (subField.type === TextCollectionFieldType.Text && subField.dropdown) {
            return (
              <div className={styles.selectContainer}>
                <span className={styles.selectTitle}>{title}</span>
                <Select
                  key={`${props.index}-${subField.field_id}`}
                  value={annotation.response?.[0]?.toString() || ''}
                  onChange={e =>
                    props.changeResponse(
                      props.index,
                      props.annotations,
                      subField.field_id,
                      e.target.value as string,
                    )
                  }
                  classes={{
                    root: classnames(styles.select, {
                      [styles.error]: !!errorText,
                    }),
                  }}
                >
                  {subField.type === TextCollectionFieldType.Text &&
                    subField.dropdown.map(v => (
                      <Option key={v} value={v}>
                        {v}
                      </Option>
                    ))}
                </Select>
              </div>
            );
          }

          return (
            <CustomTextField
              key={`${props.index}-${subField.field_id}`}
              type={inputType}
              classes={{
                root: classnames(styles.inlineTextField, {
                  // Display shorter numerical text field if there is an additional field
                  [styles.shortTextField]:
                    subField.type === TextCollectionFieldType.Number &&
                    props.filteredUnitFields.length > 1,
                }),
              }}
              index={props.index}
              disabled={props.disabled}
              setIndexValue={(_index: number, value: string) =>
                props.changeResponse(props.index, props.annotations, subField.field_id, value)
              }
              value={annotation.response?.[0]?.toString() || ''}
              title={title}
              errorText={errorText}
              onBlur={onBlur}
              shouldAutoComplete={false}
              useSaplingGrammarCheck={_.get(subField, 'use_grammar_check')}
              showWordCounter={_.get(subField, 'show_word_counter')}
              disablePasting={_.get(subField, 'disable_pasting')}
              endAdornment={
                props.quickAddText && props.index === 0 && j === 0 ? (
                  <InputAdornment position="end">
                    <TextButton
                      classes={{
                        root: styles.quickAddButton,
                        label: styles.quickAddButtonText,
                      }}
                      onClick={props.quickAddResponses}
                    >
                      {props.quickAddText}
                    </TextButton>
                  </InputAdornment>
                ) : undefined
              }
            />
          );
        })}
      </div>
      <div className={styles.inlineAction}>
        {props.showDeleteButton && (
          <IconButton
            type="button"
            classes={{ root: styles.inlineButton, label: styles.inlineButtonText }}
            disabled={props.disabled}
            onClick={() => props.delete(props.index)}
          >
            <TrashIcon />
          </IconButton>
        )}
        {props.showAddButton && (
          <>
            <div data-tip data-for={`inline-add-${props.field.field_id}-explanation`}>
              <IconButton
                type="button"
                classes={{ root: styles.inlineButton, label: styles.inlineButtonText }}
                disabled={props.disabled}
                onClick={props.addEmptyResponse && props.addEmptyResponse}
              >
                <PlusIcon />
              </IconButton>
            </div>
            <ReactTooltip id={`inline-add-${props.field.field_id}-explanation`} place="top">
              Add another {props.field.title} field (if there are multiple)
            </ReactTooltip>
          </>
        )}
      </div>
    </div>
  );
});

InlineFieldSetEntry.displayName = 'InlineFieldSetEntry';

const useFieldSetStyles = makeStyles({
  fieldSet: {
    paddingTop: '16px',
  },
  title: {
    color: '#000000',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    marginTop: '8px',
    marginBottom: '8px',
  },
  inlineFieldSetResponse: {
    background: '#FFFFFF',
    paddingTop: 8,
    paddingBottom: 8,
    marginBottom: '8px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  inlineFields: {
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1,
  },
  fieldSetHelperText: {
    marginBottom: '16px',
  },
  select: {
    minWidth: 70,
  },
  error: {
    background: 'rgba(255, 127, 127, 0.25) !important',
  },
  selectTitle: {
    color: '#000000',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    marginTop: '8px',
    marginBottom: '8px',
    display: 'inline-block',
  },
  selectContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginRight: 16,
    flexGrow: 1,
    minWidth: '50px',
    width: 'auto',
    gap: '28px',
  },
  warnBlank: {
    fontSize: '11px',
    lineHeight: '16px',
    letterSpacing: '0.1px',
    background: 'rgb(248, 187, 6, 0.3)',
    borderRadius: '2px',
    padding: '0 4px',
    color: 'rgba(0, 0, 0, 0.6)',
    display: 'inline-block',
  },
  inlineTextField: {
    marginRight: 16,
    flexGrow: 1,
    minWidth: 0,
    width: 'auto',
  },
  shortTextField: {
    maxWidth: 80,
  },
  addContainer: ({ disabled }: { disabled?: boolean }) => ({
    display: disabled ? 'none' : 'flex',
    flexDirection: 'row',
  }),
  inlineAction: {
    width: 80,
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'row',
  },
  inlineButton: {
    color: '#2B99D8',
    paddingRight: '8px !important',
    paddingLeft: '8px !important',
    margin: 0,
  },
  inlineButtonText: {
    color: '#2B99D8',
    fontWeight: 600,
    fontSize: '13px',
    lineHeight: '20px',
    letterSpacing: '0.1px',
  },
  quickAddButton: {
    color: '#2B99D8',
  },
  quickAddButtonText: {
    color: '#2B99D8',
    fontSize: '13px',
  },
});

function PlusIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12.8 6.80039H9.20003V3.20039C9.20003 2.75864 8.84177 2.40039 8.40002 2.40039H7.60002C7.15827 2.40039 6.80002 2.75864 6.80002 3.20039V6.80039H3.20002C2.75827 6.80039 2.40002 7.15864 2.40002 7.60039V8.40039C2.40002 8.84214 2.75827 9.20039 3.20002 9.20039H6.80002V12.8004C6.80002 13.2421 7.15827 13.6004 7.60002 13.6004H8.40002C8.84177 13.6004 9.20003 13.2421 9.20003 12.8004V9.20039H12.8C13.2418 9.20039 13.6 8.84214 13.6 8.40039V7.60039C13.6 7.15864 13.2418 6.80039 12.8 6.80039Z"
        fill="#2B99D8"
      />
    </svg>
  );
}

const TrashIcon = () => {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M13.2 2.39961H10.2L9.96503 1.93211C9.91524 1.83217 9.83856 1.74809 9.74361 1.68935C9.64865 1.63061 9.53918 1.59953 9.42753 1.59961H6.57002C6.45862 1.59919 6.34935 1.63015 6.25473 1.68895C6.16011 1.74776 6.08396 1.83203 6.03502 1.93211L5.80002 2.39961H2.80002C2.69394 2.39961 2.5922 2.44176 2.51718 2.51677C2.44217 2.59179 2.40002 2.69353 2.40002 2.79961V3.59961C2.40002 3.7057 2.44217 3.80744 2.51718 3.88246C2.5922 3.95747 2.69394 3.99961 2.80002 3.99961H13.2C13.3061 3.99961 13.4079 3.95747 13.4829 3.88246C13.5579 3.80744 13.6 3.7057 13.6 3.59961V2.79961C13.6 2.69353 13.5579 2.59179 13.4829 2.51677C13.4079 2.44176 13.3061 2.39961 13.2 2.39961ZM3.73002 13.2746C3.7491 13.5793 3.88356 13.8652 4.10604 14.0742C4.32851 14.2832 4.62227 14.3996 4.92752 14.3996H11.0725C11.3778 14.3996 11.6715 14.2832 11.894 14.0742C12.1165 13.8652 12.2509 13.5793 12.27 13.2746L12.8 4.79961H3.20002L3.73002 13.2746Z" />
    </svg>
  );
};
